// CalendarModal.jsx
import React from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

export default function CalendarModal({
  isMobile,
  monthNames,
  currentMonth,
  currentYear,
  showYearSelector,
  toggleYearSelector,
  goToPreviousMonth,
  goToNextMonth,
  selectMonth,
  selectYear,
  generateYearOptions,
  generateCalendarDates,
  handleDateClick,
  handleDateHover,
  isSelectedDate,
  isDateInRange,
  isToday,
  selectingStartDate,
  cancelDateSelection,
  applyDateRange,
  tempStart
}) {
  return (
    <div style={{
      position: 'absolute',
      zIndex: 30,
      marginTop: '40px',
      padding: '16px',
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      width: isMobile ? 'calc(100% - 24px)' : '280px',
      left: isMobile ? '0' : 'auto'
    }}>
      {/* Month and Year Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px'
      }}>
        <button onClick={goToPreviousMonth} style={navBtnStyle}>
          <ChevronLeft size={20} />
        </button>
        <div onClick={toggleYearSelector} style={monthYearStyle}>
          {monthNames[currentMonth]} {currentYear}
          <ChevronDown size={14} />
        </div>
        <button onClick={goToNextMonth} style={navBtnStyle}>
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Month and Year Selector */}
      {showYearSelector && (
        <div style={yearSelectorWrapper}>
          <div style={monthGridStyle}>
            {monthNames.map((month, index) => (
              <button
                key={month}
                onClick={() => selectMonth(index)}
                style={{
                  ...monthBtnStyle,
                  backgroundColor: currentMonth === index ? '#3182ce' : '#f7fafc',
                  color: currentMonth === index ? 'white' : '#4a5568'
                }}
              >
                {month.substring(0, 3)}
              </button>
            ))}
          </div>
          <div style={yearGridStyle}>
            {generateYearOptions().map(year => (
              <button
                key={year}
                onClick={() => selectYear(year)}
                style={{
                  ...monthBtnStyle,
                  backgroundColor: currentYear === year ? '#3182ce' : '#f7fafc',
                  color: currentYear === year ? 'white' : '#4a5568'
                }}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={dateLabelStyle}>
        {selectingStartDate ? 'Select start date' : 'Select end date'}
      </div>

      <div style={weekHeaderStyle}>
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
          <div key={day} style={dayLabelStyle}>{day}</div>
        ))}
      </div>

      {generateCalendarDates().map((week, weekIndex) => (
        <div key={weekIndex} style={weekRowStyle}>
          {week.map((date, dayIndex) => (
            <div
              key={dayIndex}
              onClick={() => date && handleDateClick(date)}
              onMouseOver={() => date && handleDateHover(date)}
              style={{
                textAlign: 'center',
                padding: isMobile ? '10px 0' : '6px 0',
                cursor: date ? 'pointer' : 'default',
                backgroundColor: isSelectedDate(date)
                  ? '#3182ce'
                  : isDateInRange(date)
                    ? '#ebf8ff'
                    : 'transparent',
                color: isSelectedDate(date)
                  ? 'white'
                  : isToday(date)
                    ? '#3182ce'
                    : '#4a5568',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: isToday(date) || isSelectedDate(date) ? 'bold' : 'normal',
                border: isToday(date) && !isSelectedDate(date) ? '1px solid #3182ce' : 'none'
              }}
            >
              {date ? date.getDate() : ''}
            </div>
          ))}
        </div>
      ))}

      <div style={footerActionStyle}>
        <button onClick={cancelDateSelection} style={cancelBtnStyle}>Cancel</button>
        <button
          onClick={applyDateRange}
          disabled={!tempStart}
          style={{
            ...applyBtnStyle,
            backgroundColor: tempStart ? '#3182ce' : '#a0aec0',
            cursor: tempStart ? 'pointer' : 'not-allowed'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// --- Shared styles ---
const navBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#4a5568'
};

const monthYearStyle = {
  fontWeight: 'bold',
  fontSize: '16px',
  cursor: 'pointer',
  padding: '4px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

const yearSelectorWrapper = {
  position: 'absolute',
  top: '50px',
  left: '16px',
  right: '16px',
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  zIndex: 40,
  padding: '12px',
  maxHeight: '300px',
  overflowY: 'auto'
};

const monthGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '8px',
  marginBottom: '16px'
};

const yearGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '8px'
};

const monthBtnStyle = {
  padding: '8px 4px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px'
};

const dateLabelStyle = {
  marginBottom: '10px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#4a5568'
};

const weekHeaderStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '2px',
  marginBottom: '8px'
};

const dayLabelStyle = {
  textAlign: 'center',
  padding: '4px',
  fontSize: '12px',
  fontWeight: 'bold',
  color: '#718096'
};

const weekRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '2px'
};

const footerActionStyle = {
  marginTop: '12px',
  display: 'flex',
  justifyContent: 'space-between'
};

const cancelBtnStyle = {
  padding: '8px 16px',
  backgroundColor: '#f7fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#4a5568'
};

const applyBtnStyle = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '4px',
  fontSize: '14px',
  color: 'white'
};
